<script setup lang="ts">
import { ref, computed, watch, inject, onMounted, onUnmounted } from 'vue';
import utils from '@/utils';
import { FormInputEmits, FormInputProps, FormInputRef } from '@/types/form/form-input';
import { DynamicFormContext, DynamicFormFieldValidateResult } from '@/types/form/dynamic-form';
import FormElMsg from '@/form/other/FormElMsg.vue';
import { FormElMsgData } from '@/types/form/others/form-el-msg';
import services from '@/services';
import { uniqBy } from 'lodash-es';

const props = defineProps<FormInputProps>();
const emit = defineEmits<FormInputEmits>();
const formContext = inject<DynamicFormContext>('formContext')!;
const inputRef = ref<HTMLInputElement>();
const value = ref('');
const isRequired = ref(props.item?.fields?.required?.value ?? false);
const isDisabled = ref(false);
const isVisible = ref(true);
const focused = ref(false);
const msg = ref<FormElMsgData>();

const fields = computed(() => props.item?.fields);
const inputType = computed(() => utils.sp.getVal(fields.value?.inputType));
const hasValue = computed(() => !utils.obj.isNoE(value.value));
const label = computed(() => fields.value?.label?.value || '');
const placeholder = computed(() => fields.value?.placeholder?.value || '');
const fieldName = computed(() => fields.value?.fieldName?.value || '');
const showSearchIcon = computed(() => fields.value?.showSearchIcon?.value || false);

const onMainClick = () => {
  inputRef.value!.focus();
};
const onFocus = () => {
  focused.value = true;
};
const onBlur = () => {
  focused.value = false;
};
const onInput = (e: InputEvent) => {
  const el = e.target as HTMLInputElement | undefined;
  value.value = el?.value ?? '';
  msg.value = undefined;
  emit('change', 'form-input', fieldName.value, value.value, value.value);
};
const setRequired = (required: boolean) => {
  isRequired.value = required;
};

const setDisabled = (disabled: boolean) => {
  isDisabled.value = disabled;
};

const setVisible = (visible: boolean) => {
  isVisible.value = visible;
};

const updateValue = (newValue?: string | null) => {
  value.value = newValue || '';
};

const getValue = () => {
  return value.value;
};

const onIconClick = () => {
  emit('icon-click', fieldName.value, value.value);
};

const onKeyupEnter = () => {
  if (showSearchIcon.value) {
    onIconClick();
  }
};

const validate = async () => {
  const result: DynamicFormFieldValidateResult = {
    value: value.value,
    valid: true,
    message: '',
  };

  if (!isVisible.value || !fields.value) {
    return result;
  }
  if (isRequired.value && utils.obj.isNoE(value.value)) {
    result.valid = false;
    result.message = fields.value?.requiredMsg?.value || 'This field is required';
    msg.value = {
      type: 'error',
      message: result.message,
    };
    return result;
  }
  const validators = [];
  if (inputType.value === 'tel') {
    validators.push(await services.form.getPatternValidator('Phone Validator'));
  }
  if (inputType.value === 'email') {
    validators.push(await services.form.getPatternValidator('Email Validator'));
  }
  validators.push(...fields.value.validators);
  if (fields.value.privateValidators && fields.value.privateValidators.length > 0) {
    validators.push(...fields.value.privateValidators);
  }
  uniqBy(validators, (val) => val.id);
  const validatorsError = utils.form.validateField(value.value, validators);
  if (validatorsError.length > 0) {
    result.valid = false;
    result.message = validatorsError[0];
    msg.value = {
      type: 'error',
      message: result.message,
    };
    return result;
  }
  msg.value = undefined;
  return result;
};

const getRefData = (): FormInputRef => ({
  el: inputRef.value!,
  type: 'form-input',
  name: fieldName.value,
  id: props.item.id,
  visible: isVisible.value,
  setRequired,
  setDisabled,
  setVisible,
  updateValue,
  getValue,
  validate,
});

// Register this component with parent
watch(
  () => props.item,
  () => {
    formContext.regFormEl(getRefData());
  },
  { immediate: true }
);
onMounted(() => {
  formContext.regFormEl(getRefData());
});
onUnmounted(() => {
  formContext.unRegFormEl(getRefData());
});
defineExpose(getRefData());
</script>
<template>
  <div
    :class="['f-input', { disabled: isDisabled, 'has-error': msg?.type === 'error' }]"
    v-if="fields && isVisible"
  >
    <div class="f-input__main" @click="onMainClick">
      <div class="f-input__placeholder" :class="[{ 'as-label': focused || hasValue }]">
        <span
          class="f-input__label"
          v-html="focused || hasValue ? label : utils.string.ifEmpty(placeholder, label)"
        />
        <span class="f-input__required" v-if="isRequired">*</span>
      </div>
      <input
        :type="inputType || 'text'"
        :name="`${fieldName}_${(Math.random() * 1000).toFixed(0)}`"
        :value="value"
        :disabled="isDisabled"
        @focus="onFocus"
        @blur="onBlur"
        @input="onInput"
        @keyup.enter="onKeyupEnter"
        autocomplete="new-password"
        ref="inputRef"
      />
      <Icon
        name="search"
        size="xs"
        class="f-input__search-icon"
        v-if="showSearchIcon"
        @click.stop="onIconClick"
      />
    </div>
    <form-el-msg :msg="msg" />
  </div>
</template>
<style lang="scss" scoped>
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';

.f-input {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--item-color, #000000);
  &__placeholder {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    pointer-events: none;
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--item-color, #929292);
    font-size: 16px;
    padding: 10px 16px;
    transition: all 0.2s ease-in-out;
    &.as-label {
      font-size: 12px;
      height: 36px;
      opacity: 0.7;
    }
  }

  &__main {
    position: relative;
    display: flex;
    align-items: flex-end;
    gap: 12px;
    height: 60px;
    padding: 8px 16px;
    border-radius: 4px;
    border: 1px solid var(--item-border-color, #c4c4c4);
    background: var(--item-back-color, #ffffff);

    input {
      flex: 1;
      border: none;
      background: transparent;
      font-family: 'Figtree', sans-serif;
      font-size: 16px;
      color: #000;
      outline: none;
      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:focus,
      &:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
        -webkit-text-fill-color: var(--item-color, #000) !important;
        transition: background-color 5000s ease-in-out 0s;
      }
    }
  }

  &.disabled {
    .f-input__main {
      background-color: rgba($grey, 0.3);
      cursor: not-allowed;

      input {
        cursor: not-allowed;
      }
    }
  }

  &.has-error {
    .f-input__main {
      border-color: #dc3545;
    }
  }

  &__error {
    font-size: 12px;
    color: #dc3545;
    margin-top: 4px;
  }
}
</style>
